<template>
  <el-dialog
    visible
    width="90%"
    :close-on-click-modal="false"
    :show-close="!isWriting"
    :close-on-press-escape="!isWriting"
    @close="handleClose"
  >
    <ContentHeader
      slot="title"
      :title="$t('analysesData.importValues.title')"
    />

    <el-steps
      :active="stepIndex"
      finish-status="success"
      align-center
    >
      <el-step
        v-for="key in STEP_ORDER"
        :key="key"
        :title="$t(`analysesData.importValues.steps.${key}`)"
      />
    </el-steps>

    <el-divider />

    <IgnoredRowsSummary
      v-if="showIgnoredRows"
      :rows="ignoredRows"
      class="import-values__ignored-rows"
    />

    <UploadStep
      v-if="stepKey === STEPS.UPLOAD"
      :value="input"
      :result="result"
      @input="handleInput"
      @prepared="handleUploadPrepared"
      @rejected="handleUploadRejected"
    />
    <ColumnsStep
      v-else-if="stepKey === STEPS.COLUMNS"
      :input="input"
      :columns="result.columns"
      :mode="columnsMode"
      @prepared="handleColumnsPrepared"
      @dirty="isStepDirty = $event"
    />
    <SummaryStep
      v-else-if="stepKey === STEPS.SUMMARY"
      :rows="result.rows"
      :columns="result.columns"
      :can-manage-mappings="canManageMappings"
      @editColumns="openColumns(COLUMNS_MODES.FILE)"
      @proceed="handleProceed"
      @dirty="isStepDirty = $event"
    />
    <WriteStep
      v-else
      :rows="rowsToWrite"
      :input="input"
      @writing="handleWriting"
      @completed="handleCompleted"
    />

    <!-- Writes cannot be interrupted: no way out while they run. -->
    <span
      v-if="!isWriting"
      slot="footer"
    >
      <el-button @click="handleClose">
        <!-- On the write step the footer is back only once a loop ended. -->
        {{ isWriteStep ? $t('global.close') : $t('global.cancel') }}
      </el-button>
      <!-- Unsaved edits of the step are lost going back: ask first (C58). -->
      <el-popconfirm
        v-if="canGoBack && isStepDirty"
        :title="$t('analysesData.importValues.confirmBack')"
        :confirm-button-text="$t('global.prev')"
        :cancel-button-text="$t('global.cancel')"
        class="import-values__back"
        @confirm="goTo(STEPS.UPLOAD)"
      >
        <el-button slot="reference">
          {{ $t('global.prev') }}
        </el-button>
      </el-popconfirm>
      <el-button
        v-else-if="canGoBack"
        @click="goTo(STEPS.UPLOAD)"
      >
        {{ $t('global.prev') }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
  import acl from '@/services/acl';
  import ContentHeader from '@/components/ContentHeader.vue';
  import IgnoredRowsSummary from './IgnoredRowsSummary.vue';
  import UploadStep from './UploadStep.vue';
  import ColumnsStep from './ColumnsStep.vue';
  import SummaryStep from './SummaryStep.vue';
  import WriteStep from './WriteStep.vue';
  import {
    STEPS,
    STEP_ORDER,
    COLUMNS_MODES,
    countUnmappedColumns,
    createInput,
  } from './wizardSteps';

  export default {
    name: 'ImportAnalysisValues',
    components: {
      ContentHeader,
      IgnoredRowsSummary,
      UploadStep,
      ColumnsStep,
      SummaryStep,
      WriteStep,
    },
    data() {
      return {
        STEPS,
        STEP_ORDER,
        COLUMNS_MODES,
        stepKey: STEPS.UPLOAD,
        columnsMode: COLUMNS_MODES.ALL,
        input: createInput(),
        // Last prepare response; always describes the current input.
        result: null,
        // Ignored rows of the current input when prepare refused it (C60).
        rejectedIgnoredRows: [],
        rowsToWrite: [],
        // The active step holds edits that going back would lose.
        isStepDirty: false,
        isWriting: false,
        // The last write loop ended with every row written.
        isWriteDone: false,
        // At least one value written: the list behind has to be reloaded.
        hasWritten: false,
      };
    },
    computed: {
      // Past the last step once every row is written: it gets its check mark.
      // With failed rows it stays current, where they can be retried.
      stepIndex() {
        return this.isWriteDone
          ? STEP_ORDER.length
          : STEP_ORDER.indexOf(this.stepKey);
      },
      isWriteStep() {
        return this.stepKey === STEPS.WRITE;
      },
      canGoBack() {
        return [STEPS.COLUMNS, STEPS.SUMMARY].includes(this.stepKey);
      },
      ignoredRows() {
        return this.result ? this.result.ignoredRows : this.rejectedIgnoredRows;
      },
      // Steps 1 to 3: still readable once step 1 is skipped automatically.
      showIgnoredRows() {
        return this.stepKey !== STEPS.WRITE && this.ignoredRows.length > 0;
      },
      canManageMappings() {
        return acl.userCanUse(['analysisImportMappingManage']);
      },
    },
    methods: {
      goTo(stepKey) {
        this.stepKey = stepKey;
        this.isStepDirty = false;
      },
      openColumns(mode) {
        this.columnsMode = mode;
        this.goTo(STEPS.COLUMNS);
      },
      handleInput(input) {
        this.input = input;
        this.result = null;
        this.rejectedIgnoredRows = [];
      },
      handleUploadPrepared(result) {
        this.result = result;
        this.rejectedIgnoredRows = [];
        // Blocked on the upload step, which lists the labels to fix.
        if (result.unresolvedLabels.length > 0) return;

        const unmappedCount = countUnmappedColumns(result.columns);
        if (unmappedCount === 0) {
          this.goTo(STEPS.SUMMARY);
        } else if (this.canManageMappings) {
          this.openColumns(COLUMNS_MODES.ALL);
        } else {
          this.$notify({
            showClose: true,
            type: 'error',
            message: this.$tc(
              'analysesData.importValues.unmappedColumnsNoPermission',
              unmappedCount
            ),
          });
        }
      },
      // A refused file replaces any earlier response for the same input: only
      // its rows are listed, above the file error of the upload step.
      handleUploadRejected(ignoredRows) {
        this.result = null;
        this.rejectedIgnoredRows = ignoredRows;
      },
      // Columns left unmapped here produce no rows: never loop back. A sample
      // changed since step 1 blocks there, where its label is listed (C49).
      handleColumnsPrepared(result) {
        this.result = result;
        this.goTo(
          result.unresolvedLabels.length > 0 ? STEPS.UPLOAD : STEPS.SUMMARY
        );
      },
      handleProceed(rows) {
        this.rowsToWrite = rows;
        this.goTo(STEPS.WRITE);
      },
      handleWriting() {
        this.isWriting = true;
        this.isWriteDone = false;
      },
      handleCompleted({ written, failed }) {
        this.isWriting = false;
        this.isWriteDone = failed === 0;
        this.hasWritten = written > 0;
      },
      handleClose() {
        this.$emit('dialogClose', { success: this.hasWritten });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .import-values__ignored-rows {
    margin-bottom: 1rem;
  }

  // Element's `.el-button + .el-button` margin does not reach a button
  // wrapped in el-popconfirm.
  .import-values__back {
    margin-left: 0.625rem;
  }
</style>
